<template>
    <div class="version-summary-modal">
        <el-dialog
            v-model="isVisible"
            width="1200px"
            :show-close="false"
            class="custom-modal"
            @close="close"
            :title="$trans('profile_dossier.version_history') || 'Version History'"
        >
            <span class="dialog-cancel-text" @click="close">
                {{ $trans("messages.close") || "Close" }}
                <el-icon><Close /></el-icon>
            </span>

            <div class="table-container" v-loading="isLoading">
                <EasyDataTable
                    :headers="headers"
                    :items="historyData"
                    :rows-per-page="5"
                    class="dataTable"
                    table-class-name="history-table"
                    buttons-pagination
                >
                    <template #item-created_at="{ created_at }">
                        {{ formatDate(created_at) }}
                    </template>

                    <template #item-user_name="{ user_name }">
                        {{ user_name ?? "System" }}
                    </template>
                    
                    <template #item-new_value="{ new_value }">
                        <div class="truncate-text">
                            {{ stripHtml(new_value) }}
                        </div>
                    </template>
                </EasyDataTable>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { Close } from "@element-plus/icons-vue";
import EasyDataTable from "vue3-easy-data-table";
import axios from "axios";

export default {
    name: "VersionSummaryModal",
    components: { Close, EasyDataTable },
    data() {
        return {
            isVisible: false,
            isLoading: false,
            historyData: [],
            headers: [
                {
                    text: this.$trans("profile_dossier.date") || "Date",
                    value: "created_at",
                    sortable: true,
                    width: 160,
                },
                {
                    text: this.$trans("profile_dossier.user") || "User",
                    value: "user_name",
                    sortable: true,
                    width: 200,
                },
                {
                    text: this.$trans("profile_dossier.value") || "Content History",
                    value: "new_value",
                },
            ],
        };
    },
    methods: {
        async open(dossierId) {
            this.isVisible = true;
            this.isLoading = true;
            try {
                const response = await axios.get(
                    `/profile-dossier/${dossierId}/versions`,
                );
                this.historyData = response.data;
            } catch (error) {
                this.$message.error("Failed to fetch history.");
            } finally {
                this.isLoading = false;
            }
        },
        close() {
            this.isVisible = false;
        },
        stripHtml(html) {
            if (!html) return '';
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent || "";
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            return `${day}.${month}.${year} ${hours}:${minutes}`;
        }
    },
};
</script>

<style scoped>
.version-summary-modal :deep(.el-dialog) {
    --el-dialog-width: 1200px;
    top: -50px !important;
}

:deep(.el-dialog__header) {
    padding-bottom: 20px;
    border-bottom: 1px solid #ebeef5;
}

.table-container {
    width: 100%;
    box-sizing: border-box;
    padding-top: 10px;
    font-family: "Barlow", sans-serif;
    background-color: #ededed;
    padding-bottom: 60px;
}

.history-table {
    width: 100%;
}

:deep(.vue3-easy-data-table) {
    margin-bottom: unset !important;
    border: none !important;
}

:deep(.vue3-easy-data-table tr td) {
    height: 1px;
}

:deep(.vue3-easy-data-table tr th),
:deep(.vue3-easy-data-table tr td) {
    border: 2px solid #ededed;
}

:deep(.vue3-easy-data-table tr td) {
    background: #ffffff;
    padding: 0.5rem;
    line-height: 20px;
}

:deep(.vue3-easy-data-table tr th) {
    border-top: 0;
    border-bottom: 0;
    background: rgb(237, 237, 237);
    padding: 0 0.5rem;
    color: #3c3c3c;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    height: 24px;
    line-height: 24px;
    font-family: sans-serif;
    border-top: unset;
}

:deep(.vue3-easy-data-table__footer) {
    background-color: transparent !important;
    justify-content: center !important;
}

:deep(.pagination__items-index) {
    display: none !important;
}

:deep(.buttons-pagination .item.button.active) {
    color: #fff;
    background: linear-gradient(
        90deg,
        rgb(65, 171, 196) 0%,
        rgb(119, 214, 241) 100%
    );
    border-color: rgb(65, 171, 196);
    border-radius: 0;
}

:deep(.buttons-pagination .item.button) {
    border-radius: 0;
}

:deep(.buttons-pagination) {
    --40dd4f07: unset !important;
    transform: scale(1.5);
    margin-top: 25px;
}

:deep(.vue3-easy-data-table__main) {
    background-color: #ededed;
}

:deep(.next-page__click-button .arrow) {
    margin-top: 21px;
    margin-left: 40px;
}

:deep(.previous-page__click-button .arrow) {
    margin-right: 40px;
    margin-top: 21px;
}

:deep(.pagination__rows-per-page) {
    display: none !important;
}

.history-table {
    --easy-table-header-font-size: 14px;
    --easy-table-body-row-font-size: 14px;
    --easy-table-body-row-hover-background-color: #fff;
}

.dialog-cancel-text {
    position: absolute;
    top: 5px;
    right: 21px;
    cursor: pointer;
    font-size: 14px;
    color: #909399;
    font-weight: 500;
    z-index: 2000;
    user-select: none;
}

.dialog-cancel-text:hover {
    color: #41abc4;
}

.dialog-cancel-text :deep(.el-icon) {
    top: 3px;
    transform: scale(1.4);
}

:deep(.el-dialog__title) {
    font-size: 1.5rem;
}

.truncate-text {
    max-height: 100px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
}
</style>